import { useState } from 'react'

import { Card, LoaderCircle } from '@skytech/ui'
import { Badge } from '@skytech/ui/Badge'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { ExternalLink } from '@skytech/ui/ExternalLink'
import { useQuery } from '@tanstack/react-query'
import CancelIcon from 'mdi-react/CancelIcon'
import { useNavigate, useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { LoadingError } from '@/components/LoadingError'
import { LocalDate } from '@/components/LocalDate'
import { USER_ROLE } from '@/hooks/access'
import useUser from '@/hooks/user'
import terminationResources from '@/modules/subscriptions/termination/resources'
import { FORM_URL } from '@/util/misc'

import { SUB_SERVICE_ORDER_STATUS } from '../constants'

import { CancelTerminationModal } from './CancelTerminationModal'
import queries from './queries'

import './TerminationPage.styles.scss'

function getStatusBadge(statusId: number) {
  if ([SUB_SERVICE_ORDER_STATUS.NEW, SUB_SERVICE_ORDER_STATUS.COMPLETED].includes(statusId))
    return 'positive'
  if ([SUB_SERVICE_ORDER_STATUS.TERMINATED, SUB_SERVICE_ORDER_STATUS.CANCELLED].includes(statusId))
    return 'negative'

  return 'neutral'
}

export const TerminationPage = () => {
  const translate = useTranslation()
  const navigate = useNavigate()
  const user = useUser()
  const params = useParams()
  const terminationOrderId = Number(params.terminationOrderId)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)

  if (isNaN(terminationOrderId)) throw Error(translate('Termination order ID is not a number!'))

  const { data: termination, isError } = useQuery(queries.termination(terminationOrderId), () =>
    terminationResources.get(terminationOrderId)
  )

  if (isError) {
    return (
      <Container onSwipedLeft={() => navigate(-1)}>
        <LoadingError />
      </Container>
    )
  }

  if (!termination) {
    return (
      <Container onSwipedLeft={() => navigate(-1)}>
        <LoaderCircle />
      </Container>
    )
  }

  const terminationStatus = Object.entries(SUB_SERVICE_ORDER_STATUS).find(
    ([, val]) => val === termination.status
  )?.[0]
  const isFormLinkVisible = user?.role !== USER_ROLE.IT_USER

  return (
    <Container onSwipedLeft={() => navigate(-1)}>
      <div className="termination">
        <div className="header">
          <Container.Header>
            <Translation id="termination" /> {termination.id}
          </Container.Header>

          <div className="status">
            <Badge type={getStatusBadge(termination.status)}>
              <Translation id={`sub_service_order_status/${terminationStatus}`} />
            </Badge>
          </div>
        </div>

        <section className="sub-data-points">
          <DataPoints>
            <div>
              {isFormLinkVisible && (
                <DataPoint
                  header={<Translation id="Form" />}
                  value={
                    <ExternalLink
                      withIcon
                      size="semi-large"
                      href={FORM_URL.replace(':code', termination.code)}
                    >
                      <Translation id="View" />
                    </ExternalLink>
                  }
                />
              )}

              {termination.apiSupplierOrderStatus && (
                <DataPoint
                  header={<Translation id="Carrier status" />}
                  value={termination.apiSupplierOrderStatus}
                />
              )}

              {termination.apiSupplierOrderStatusReason && (
                <DataPoint
                  header={<Translation id="Reason" />}
                  value={termination.apiSupplierOrderStatusReason}
                />
              )}

              <DataPoint
                header={<Translation id="Dealer comment" />}
                value={termination.dealerComment || '-'}
              />
            </div>
          </DataPoints>
        </section>

        <section className="termination-info-cards">
          <Card>
            <h2 className="mt-0">
              <Translation id="User" />
            </h2>
            <DataPoints>
              <DataPoint header={<Translation id="Employee name" />} value={termination.name} />

              <DataPoint
                header={<Translation id="Customer name" />}
                value={termination.customerName}
              />

              <DataPoint header={<Translation id="Phone number" />} value={termination.phoneNo} />

              <DataPoint header={<Translation id="Email" />} value={termination.email} />
            </DataPoints>
          </Card>

          <Card>
            <h2 className="mt-0">
              <Translation id="Termination order" />
            </h2>

            <DataPoints>
              <DataPoint
                header={<Translation id="Created" />}
                value={<LocalDate date={termination.created} />}
              />

              <DataPoint
                header={<Translation id="Updated" />}
                value={<LocalDate date={termination.updated} />}
              />

              <DataPoint
                header={<Translation id="Type" />}
                value={<Translation id={`termination_type/${termination.type}`} />}
              />

              <DataPoint header={<Translation id="Comment" />} value={termination.comment} />
            </DataPoints>
          </Card>
        </section>
      </div>

      <div className="mr-4 flex justify-between">
        <Button variant="tertiary" onClick={() => navigate(-1)}>
          <Translation id="Back to list" />
        </Button>
        <Button
          Icon={<CancelIcon />}
          onClick={() => setIsCancelModalOpen(true)}
          disabled={termination.status !== 0}
        >
          <Translation id="Cancel Order" />
        </Button>
      </div>

      <CancelTerminationModal
        id={termination.id}
        isOpen={isCancelModalOpen && termination.status === 0}
        onClose={() => setIsCancelModalOpen(false)}
      />
    </Container>
  )
}
